/**
 * BorsihindPage — Nord Pool börsihinna + ostu/müügi-hinna graafik.
 *
 * Andmed Elering API'st (https://dashboard.elering.ee/api/nps/price).
 * Tariifid salvestatakse SQLite config-tabelisse võtmega `tariffs`.
 *
 * Ostuhind = (turuhind + võrgutasu + ülekandetasu + aktsiis + marginaal) × (1 + KM%)
 * Müügihind = turuhind − võrgutasu − marginaal + taastuvenergia toetus
 *
 * Copyright (c) 2025 Rohepaneel
 */
import { useState, useEffect, useMemo } from 'react';
import { Header } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine,
} from 'recharts';

const PERIODS = [
  { id: 'today',     label: 'Täna' },
  { id: 'tomorrow',  label: 'Homme' },
  { id: 'yesterday', label: 'Eile' },
  { id: '7d',        label: '7 päeva' },
  { id: '30d',       label: '30 päeva' },
];

function fmtTime(ts, period) {
  const d = new Date(ts);
  if (period === '7d' || period === '30d') {
    return d.toLocaleDateString('et-EE', { day: 'numeric', month: 'short' }) + ' ' +
           d.toLocaleTimeString('et-EE', { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleTimeString('et-EE', { hour: '2-digit', minute: '2-digit' });
}

function computeRange(period) {
  const now = new Date();
  if (period === 'today') {
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    return { from: start, to: start + 86400e3 - 1 };
  }
  if (period === 'tomorrow') {
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1).getTime();
    return { from: start, to: start + 86400e3 - 1 };
  }
  if (period === 'yesterday') {
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1).getTime();
    return { from: start, to: start + 86400e3 - 1 };
  }
  const days = period === '7d' ? 7 : 30;
  return { from: now.getTime() - days * 86400e3, to: now.getTime() };
}

export function BorsihindPage({ wsConnected }) {
  const { user } = useAuth();
  // Tariifid on globaalsed (kõigi klientide tulu-arvutus) → muuta saab ainult superadmin
  const canEditTariffs = !!user?.is_superadmin;

  const [period, setPeriod] = useState('today');
  const [points, setPoints] = useState([]);
  const [tariffs, setTariffs] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [updated, setUpdated] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Lae hinnad
  useEffect(() => {
    let cancel = false;
    async function load() {
      setLoading(true);
      setError(null);
      const range = computeRange(period);
      try {
        const r = await fetch(`/api/nordpool/prices?from=${range.from}&to=${range.to}`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        if (cancel) return;
        setPoints(j.points || []);
        setTariffs(j.tariffs || null);
        setUpdated(Date.now());
      } catch (err) {
        if (!cancel) setError(err.message);
      } finally {
        if (!cancel) setLoading(false);
      }
    }
    load();
    return () => { cancel = true; };
  }, [period]);

  // Andmed graafikule (c/kWh, mitte EUR/kWh — lihtsam vaadata)
  const chartData = useMemo(() => points.map(p => ({
    ts: p.ts,
    time: fmtTime(p.ts, period),
    market: (p.price_kwh || 0) * 100,       // c/kWh
    buy: (p.price_buy_kwh || 0) * 100,
    sell: (p.price_sell_kwh || 0) * 100,
  })), [points, period]);

  // Statistika
  const stats = useMemo(() => {
    if (chartData.length === 0) return null;
    const market = chartData.map(d => d.market).filter(v => v != null);
    if (market.length === 0) return null;
    // Leia praeguse tunni hind — punkt, mille ts on praegusest ajast ≤ 1 tund tagasi
    const now = Date.now();
    const currentPoint = chartData.find(d => d.ts <= now && (now - d.ts) < 3600_000);
    return {
      market_now: currentPoint?.market ?? null,
      buy_now: currentPoint?.buy ?? null,
      sell_now: currentPoint?.sell ?? null,
      market_min: Math.min(...market),
      market_max: Math.max(...market),
      market_avg: market.reduce((a, b) => a + b, 0) / market.length,
      buy_avg: chartData.reduce((a, b) => a + b.buy, 0) / chartData.length,
      sell_avg: chartData.reduce((a, b) => a + b.sell, 0) / chartData.length,
    };
  }, [chartData]);

  // Praegune aeg ms (ReferenceLine joone jaoks) — uuendub iga 60s
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const iv = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(iv);
  }, []);

  // Praeguse aja string-väärtus, mis vastab XAxis dataKey="time"-le.
  // XAxis on kategooriline (mitte numbriline), nii et ReferenceLine peab kasutama sama time-string-i mis on lähima andmepunkti küljes.
  const nowLabel = useMemo(() => {
    if (chartData.length === 0) return null;
    if (now < chartData[0].ts || now > chartData[chartData.length - 1].ts) return null;
    let closest = chartData[0];
    let minDiff = Math.abs(now - closest.ts);
    for (const p of chartData) {
      const diff = Math.abs(now - p.ts);
      if (diff < minDiff) { minDiff = diff; closest = p; }
    }
    return closest.time;
  }, [chartData, now]);

  return (
    <div className="h-full flex flex-col" style={{ background: 'var(--bg)' }}>
      <Header title="Börsihind" connected={wsConnected} lastUpdate={updated} />

      <div style={{ flex: 1, overflow: 'auto', padding: '20px 28px 40px', maxWidth: 1480, width: '100%', margin: '0 auto' }}>
        {/* Filtrid */}
        <div style={{
          display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 20,
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 6, padding: '14px 16px', alignItems: 'center',
        }}>
          <div>
            <div style={{ fontSize: '0.62rem', color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
              Periood
            </div>
            <div style={{ display: 'flex', gap: 4 }}>
              {PERIODS.map(p => (
                <button key={p.id} onClick={() => setPeriod(p.id)}
                  style={{ padding: '5px 11px', borderRadius: 4,
                    border: period === p.id ? '1px solid #18181b' : '1px solid var(--border)',
                    background: period === p.id ? '#18181b' : 'white',
                    color: period === p.id ? 'white' : 'var(--text-2)',
                    fontSize: '0.7rem', cursor: 'pointer' }}>
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <div style={{ flex: 1 }} />

          <button
            onClick={() => setSettingsOpen(true)}
            disabled={!canEditTariffs}
            title={canEditTariffs ? 'Muuda võrgutasusid ja marginaali' : 'Ainult superadmin (tariifid on kõigile klientidele ühised)'}
            style={{
              padding: '8px 14px', borderRadius: 4,
              border: '1px solid var(--border-strong)',
              background: canEditTariffs ? 'white' : '#f3f4f6',
              color: canEditTariffs ? 'var(--text)' : 'var(--text-3)',
              fontSize: '0.78rem', fontWeight: 500,
              cursor: canEditTariffs ? 'pointer' : 'not-allowed',
              display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            ⚙ Tariifid {canEditTariffs ? '' : '(superadmin)'}
          </button>
        </div>

        {/* Statistika */}
        {stats && (
          <div style={{
            display: 'grid', gap: 1, marginBottom: 16,
            gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
            background: 'var(--border)', border: '1px solid var(--border)',
            borderRadius: 6, overflow: 'hidden',
          }}>
            <StatCell label="Turuhind hetkel" value={stats.market_now} unit="c/kWh" color="#0e7490" bold />
            <StatCell label="Turuhind min" value={stats.market_min} unit="c/kWh" color="#0e7490" />
            <StatCell label="Turuhind keskmine" value={stats.market_avg} unit="c/kWh" color="#0891b2" />
            <StatCell label="Turuhind max" value={stats.market_max} unit="c/kWh" color="#06b6d4" />
            <StatCell label="Ostuhind keskmine" value={stats.buy_avg} unit="c/kWh" color="#b91c1c" />
            <StatCell label="Müügihind keskmine" value={stats.sell_avg} unit="c/kWh" color="#15803d" />
          </div>
        )}

        {/* Graafik */}
        <div style={{
          background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6,
          padding: 16, height: 460,
        }}>
          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--text-3)' }}>
              Laadin Nord Pool hindu...
            </div>
          ) : error ? (
            <div style={{ color: '#dc2626', padding: 20 }}>
              Viga: {error}
            </div>
          ) : chartData.length === 0 ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--text-3)' }}>
              Selle perioodi kohta hindu pole.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f1f4" />
                <XAxis dataKey="time" stroke="#71717a" fontSize={11} />
                <YAxis
                  stroke="#71717a" fontSize={11}
                  label={{ value: 'c/kWh', position: 'insideLeft', angle: -90, fill: '#71717a', fontSize: 12 }}
                  tickFormatter={(v) => v.toFixed(1)}
                />
                <Tooltip
                  contentStyle={{ background: 'white', border: '1px solid var(--border-strong)', borderRadius: 4 }}
                  formatter={(v, name) => [`${Number(v).toFixed(2)} c/kWh`, name]}
                />
                <Legend wrapperStyle={{ fontSize: '0.78rem' }} />
                <ReferenceLine y={0} stroke="#94a3b8" strokeDasharray="3 3" />
                {/* Praeguse aja vertikaalne joon — XAxis on kategooriline, kasutame lähima punkti time-string-i */}
                {nowLabel && (
                  <ReferenceLine
                    x={nowLabel}
                    stroke="#dc2626"
                    strokeWidth={1.5}
                    strokeDasharray="4 2"
                    label={{ value: 'praegu', position: 'insideTopLeft', fill: '#dc2626', fontSize: 10, fontWeight: 600, offset: 6 }}
                  />
                )}
                <Line type="monotone" dataKey="market" name="Turuhind"  stroke="#0e7490" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="buy"    name="Ostuhind"  stroke="#dc2626" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="sell"   name="Müügihind" stroke="#15803d" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Selgitus */}
        {tariffs && (
          <div style={{
            marginTop: 12, padding: '10px 14px',
            background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6,
            fontSize: '0.7rem', color: 'var(--text-3)',
          }}>
            <strong>Hinna arvutus:</strong>
            <br />
            • <span style={{ color: '#0e7490' }}>Turuhind</span> = Nord Pool spot (EE) — c/kWh
            <br />
            • <span style={{ color: '#dc2626' }}>Ostuhind</span> = (turuhind + võrgutasu {tariffs.buy.network_fee}c + ülekanne {tariffs.buy.transmission_fee}c + varustuskindlus {tariffs.buy.supply_security_fee || 0}c + tasakaalustus {tariffs.buy.balancing_fee || 0}c + aktsiis {tariffs.buy.electricity_excise}c + marginaal {tariffs.buy.sales_margin}c) × {(1 + tariffs.buy.vat_percent / 100).toFixed(2)} (KM {tariffs.buy.vat_percent}%)
            <br />
            • <span style={{ color: '#15803d' }}>Müügihind</span> = turuhind − võrgutasu {tariffs.sell.network_fee}c − marginaal {tariffs.sell.sales_margin}c + tarvkvarum {tariffs.sell.renewable_support}c
          </div>
        )}
      </div>

      {/* Settings modal */}
      {settingsOpen && (
        <TariffsModal
          tariffs={tariffs}
          onSave={async (next) => {
            const r = await fetch('/api/tariffs', {
              method: 'PUT', credentials: 'same-origin',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(next),
            });
            if (r.ok) {
              const j = await r.json();
              setTariffs(j.tariffs);
              setSettingsOpen(false);
              // Reload prices to re-compute with new tariffs
              const range = computeRange(period);
              const r2 = await fetch(`/api/nordpool/prices?from=${range.from}&to=${range.to}`);
              if (r2.ok) {
                const j2 = await r2.json();
                setPoints(j2.points || []);
              }
            } else {
              alert('Tariifide salvestamine ebaõnnestus');
            }
          }}
          onCancel={() => setSettingsOpen(false)}
        />
      )}
    </div>
  );
}

function StatCell({ label, value, unit, color, bold }) {
  return (
    <div style={{
      background: bold ? `${color}0d` : 'var(--surface)',
      padding: '12px 16px',
      borderLeft: bold ? `3px solid ${color}` : undefined,
    }}>
      <div style={{ fontSize: '0.62rem', color, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: bold ? 700 : 500, marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: bold ? '1.45rem' : '1.2rem', fontWeight: bold ? 700 : 600, color: bold ? color : 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>
        {value != null ? value.toFixed(2) : '—'}
        <span style={{ fontSize: '0.6em', color: 'var(--text-3)', marginLeft: 4 }}>{unit}</span>
      </div>
    </div>
  );
}

function TariffsModal({ tariffs, onSave, onCancel }) {
  const [draft, setDraft] = useState({
    buy: { ...(tariffs?.buy || {}) },
    sell: { ...(tariffs?.sell || {}) },
  });

  const setBuy = (k, v) => setDraft(d => ({ ...d, buy: { ...d.buy, [k]: parseFloat(v) || 0 } }));
  const setSell = (k, v) => setDraft(d => ({ ...d, sell: { ...d.sell, [k]: parseFloat(v) || 0 } }));

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000,
    }} onClick={onCancel}>
      <div style={{
        background: 'white', borderRadius: 8, padding: 24,
        maxWidth: 560, width: '90%', maxHeight: '90vh', overflow: 'auto',
      }} onClick={(e) => e.stopPropagation()}>
        <h2 style={{ margin: '0 0 16px', fontSize: '1.1rem' }}>Tariifide seadistus</h2>

        <h3 style={{ fontSize: '0.85rem', color: '#dc2626', marginTop: 0, marginBottom: 8 }}>Ostuhind (võrgust)</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px 30px', gap: 8, alignItems: 'center', marginBottom: 16 }}>
          <TariffInput label="Võrgutasu"             value={draft.buy.network_fee} onChange={(v) => setBuy('network_fee', v)} />
          <TariffInput label="Ülekandetasu"          value={draft.buy.transmission_fee} onChange={(v) => setBuy('transmission_fee', v)} />
          <TariffInput label="Varustuskindluse tasu" value={draft.buy.supply_security_fee || 0} onChange={(v) => setBuy('supply_security_fee', v)} />
          <TariffInput label="Tasakaalustusvõims."   value={draft.buy.balancing_fee || 0} onChange={(v) => setBuy('balancing_fee', v)} />
          <TariffInput label="Elektriaktsiis"        value={draft.buy.electricity_excise} onChange={(v) => setBuy('electricity_excise', v)} />
          <TariffInput label="Müügimarginaal"        value={draft.buy.sales_margin} onChange={(v) => setBuy('sales_margin', v)} />
          <TariffInput label="Käibemaks (%)"         value={draft.buy.vat_percent} unit="%" onChange={(v) => setBuy('vat_percent', v)} />
        </div>

        <h3 style={{ fontSize: '0.85rem', color: '#15803d', marginTop: 0, marginBottom: 8 }}>Müügihind (võrku)</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px 30px', gap: 8, alignItems: 'center', marginBottom: 16 }}>
          <TariffInput label="Võrgutasu"           value={draft.sell.network_fee} onChange={(v) => setSell('network_fee', v)} />
          <TariffInput label="Müügimarginaal"      value={draft.sell.sales_margin} onChange={(v) => setSell('sales_margin', v)} />
          <TariffInput label="Taastuvenergia toetus" value={draft.sell.renewable_support} onChange={(v) => setSell('renewable_support', v)} />
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
          <button onClick={onCancel}
            style={{ padding: '8px 16px', border: '1px solid var(--border)', borderRadius: 4, background: 'white', cursor: 'pointer' }}>
            Tühista
          </button>
          <button onClick={() => onSave(draft)}
            style={{ padding: '8px 16px', border: 'none', borderRadius: 4, background: '#0f172a', color: 'white', cursor: 'pointer', fontWeight: 500 }}>
            Salvesta
          </button>
        </div>
      </div>
    </div>
  );
}

function TariffInput({ label, value, unit = 'c/kWh', onChange }) {
  return (
    <>
      <label style={{ fontSize: '0.82rem' }}>{label}</label>
      <input
        type="number" step="0.001" value={value ?? 0}
        onChange={e => onChange(e.target.value)}
        style={{ padding: '6px 8px', border: '1px solid var(--border-strong)', borderRadius: 4, fontSize: '0.82rem', textAlign: 'right' }}
      />
      <span style={{ fontSize: '0.72rem', color: 'var(--text-3)' }}>{unit}</span>
    </>
  );
}

export default BorsihindPage;
